/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

export interface Order {
  quantity: number;
  unit: string;
  createdAt: string;
}

export enum LifeCycleState {
  STOCKED = 'Active- Stocked',
  NOT_STOCKED = 'Active- not stocked',
  OBSOLETE = 'Obsolete All sites',
  DISCONTINUED = 'Discontinued Item',
  END_OF_LIFE = 'End of Life',
  TEMPORARILY_UNAVAILABLE = 'Temporarily Unavailable',
}

export interface ProductApiResponseItem {
  basePriceExcGst?: number;
  category: string;
  colour?: string;
  colourDescription?: string;
  description: string;
  discountedPrice?: number;
  discountedPriceExcGst?: number;
  discountedPriceIncGst?: number;
  imageUrl: string;
  isPriceNegotiable: boolean;
  itemNumber: string;
  lifeCycleState: string;
  masterProductId: number;
  name: string;
  nationalPrice?: number;
  onlineL1Category?: string;
  onlineL2Category?: string;
  orders: Order[];
  productId: number;
  productVariantNumber: string;
  size?: string;
  sizeDescription?: string;
  stockAvailability?: string;
  alternativeProduct?: Product;
}

export interface Product {
  basePriceExcGst: number;
  basePriceIncGst: number;
  category: string;
  subCategory?: string;
  colour?: string;
  colourDescription?: string;
  name: string;
  description: string;
  discountedPriceExcGst: number;
  discountedPriceIncGst: number;
  editedPriceExcGst?: number;
  originalPriceExcGst?: number;
  lastEditedReasonCode?: string | null;
  lastEditedReasonNote?: string | null;
  imageUrl?: string;
  isPriceNegotiable?: boolean;
  itemNumber: string;
  lifeCycleState: string | null;
  masterProductId: number;
  orders: Order[];
  productId: number;
  size?: string;
  sizeDescription?: string;
  hasVariants?: boolean;
  alternativeProduct?: Product;
  stockAvailability?: string;
  stockAvailabilities?: StockAvailabilityType[];
}

export interface ProductsByAccountDictionary {
  [index: string]: Product[];
}

export interface ProductsByProductIdDictionary {
  [index: string]: Product;
}

export interface ProductsApiResponse {
  products: ProductApiResponseItem[];
}

export interface ProductsReducerPayload {
  products: Product[];
}

export type MediaLocationType = {
  uri: string;
  isDefault: boolean;
};

export interface Variant {
  productId: number;
  productVariantNumber: string;
  name: string;
  dimensions: Dimension[];
}

export interface Dimension {
  type: number;
  id: string;
  value: string;
  displayOrder?: number | null;
}

export interface DetailsApiResponse {
  name: string;
  brand: string;
  onlineShortDescription: string;
  onlineLongDescription: string;
  basePriceExcGst: number;
  discountedPriceExcGst: number;
  variants?: Variant[];
  mediaLocations?: MediaLocationType[];
}
export type ProductDetailsApiResponse = Product & DetailsApiResponse;

export interface GetProductsReducerAction extends ProductsReducerPayload {
  accountNumber: string;
}

export interface StatusDictionary {
  [index: string]: 'idle' | 'pending' | 'fulfilled';
}

export interface ProductsState {
  products: ProductsByAccountDictionary;
  status: StatusDictionary;
}

export interface StocksState {
  stocks: ProductsByProductIdDictionary;
}

export interface StockAvailabilitiesPayload {
  productIds: number[] | undefined;
}
export type StockAvailabilityType = {
  productId: number;
  inventLocationId: string;
  inventoryLevelLabel: string;
  inventoryLevelCode: string;
  isBackOrderAllowed: boolean;
  quantity: number;
};
export interface StockAvailabilities {
  availabilities: StockAvailabilityType[];
}
